import set from 'lodash/set';
import omit from 'lodash/omit';
import get from 'lodash/get';
import pick from 'lodash/pick';
import cloneDeep from 'lodash/cloneDeep';

import {processAPIResponse} from '@/utils/request';
import {
  fetchIntegrationTypesRequest,
  fetchPartnerConfigRequest,
  fetchPartnerUserIntegrationsRequest,
  fetchUiPreferencesRequest,
  setPartnerConfigRequest,
  setPartnerPreferencesRequest,
  setUiPreferencesRequest,
} from '@/services/api/preferences';
import * as PreferencesSelectors from '@/selectors/preferencesSelectors';
import {
  FILTER_PLACE_TO_VISIBILITY_SETTINGS_MAPPING,
  FILTER_PLACES,
  VISIBILITY_SETTINGS_PLACES_TO_FILTER_PLACE_MAPPING,
} from '@/components/Filters/LeadFilters';
import {SAVED_VIEW_SCOPES} from '@/components/Preferences/constants';
import {getQuickDiffDateRange} from '@/utils/date';
import {LEAD_VISIBILITY_SETTINGS_PLACES} from '@/components/Kanban/VisibilitySettings/constants';
// Chart/table configuration, not filters — survives "Clear filters" so the
// report pages keep their metric and axis when the user resets filtering
import {CHART_CONFIG_FILTER_KEYS as GROUPING_FILTER_KEYS} from '@/constants/filters';

// Place filters are persisted with concrete dates; when a relative preset
// (quickDiff) was active, refresh its window on fetch so "Last 30 days"
// doesn't freeze to the dates it had when it was last saved.
function refreshQuickDiffDateFilters(data) {
  if (!data?.ui) return data;
  const ui = { ...data.ui };
  for (const [place, settings] of Object.entries(ui)) {
    const quickDiff = settings?.filters?.date?.quickDiff;
    const range = quickDiff && getQuickDiffDateRange(quickDiff);
    if (range) {
      ui[place] = {
        ...settings,
        filters: { ...settings.filters, date: { ...settings.filters.date, ...range } },
      };
    }
  }
  return { ...data, ui };
}

const initialState = {
  list: {},
  partnerConfig: {},
  initialized: false,
  userIntegrations: false,
  integrationTypes: false,
};


export default {
  namespace: 'preferences',

  state: initialState,

  effects: {
    * fetch({ then }, { call, put, select }) {
      const { response, data } = yield call(fetchUiPreferencesRequest);
      yield put({
        type: 'fetchPartnerConfig',
      });
      return yield processAPIResponse({ response, data }, {
        * onSuccess(data) {
          yield put({
            type: 'set',
            payload: refreshQuickDiffDateFilters(data),
          });
          if (then) then(data);
        },
        * onError(error) {
          if (then) then(null);
        },
      });
    },
    // Region Saved Views
    * setActiveSavedView({ payload }, { call, put, select }) {
      const placeSettings = yield select(PreferencesSelectors.selectPlaceSettings, payload.place) || {};
      const views = yield select(PreferencesSelectors.selectSavedViewsForPlace, payload.place) || [];
      const view = views.find(view => view.id.toString() === payload.viewId.toString());
      const visibilitySettings = FILTER_PLACE_TO_VISIBILITY_SETTINGS_MAPPING[payload.place];
      if (Array.isArray(visibilitySettings)) {
        for (const key of visibilitySettings) {
          if (view.preferences?.[key]) {
            yield put({
              type: 'update',
              payload: {
                key,
                silent: true,
                value: {
                  ...get(placeSettings, `preferences.${key}`, {}),
                  ...view.preferences[key],
                },
              },
            });
          }
        }
      }

      // Views saved with a relative preset ("Last 30 days") keep the preset in
      // date.quickDiff — resolve it to a fresh range on apply so the window
      // stays relative. Views with explicit dates (no quickDiff) stay as-is.
      let filters = view.filters || {};
      const quickDiffRange = filters.date?.quickDiff && getQuickDiffDateRange(filters.date.quickDiff);
      if (quickDiffRange) {
        filters = { ...filters, date: { ...filters.date, ...quickDiffRange } };
      }

      yield put({
        type: 'update',
        payload: {
          key: payload.place,
          value: {
            ...placeSettings,
            activeView: payload.viewId,
            activeViewChanged: false,
            filters,
            preferences: view.preferences || {},
          },
        },
      });
    },
    * togglePinView({ payload }, { call, put, select }) {
      const {viewId, place} = payload;
      const placeSettings = yield select(PreferencesSelectors.selectPlaceSettings, place) || {};
      let {pinnedViews = []} = placeSettings;
      const newPinnedViews = pinnedViews.includes(viewId) ? pinnedViews.filter(id => id !== viewId) : [...pinnedViews, viewId];
      yield put({
        type: 'update',
        payload: {
          key: payload.place,
          value: {
            ...placeSettings,
            pinnedViews: newPinnedViews
          },
        },
      });
    },
    * updateView({ payload }, { call, put, select }) {
      const { place, viewId } = payload;
      const allViews = yield select(PreferencesSelectors.selectSavedViewsForPlace, place) || [];
      const scope = allViews.find(v => v.id.toString() === viewId.toString()).scope;
      const views = yield select(PreferencesSelectors.selectSavedViewsForPlace, place, scope) || [];
      const placeSettings = yield select(PreferencesSelectors.selectPlaceSettings, place, scope) || {};
      yield put({
        type: 'update',
        payload: {
          scope,
          key: place,
          value: {
            ...placeSettings,
            savedViews: views.map(v => {
              if (v.id.toString() === payload.viewId.toString()) {
                return {
                  ...v,
                  ...omit(payload, ['place', 'viewId']),
                };
              }
              return v;
            }),
          },
        },
      });
    },
    * modifySavedView({ payload }, { call, put, select }) {
      const { scope, place } = payload;
      const placeSettings = yield select(PreferencesSelectors.selectPlaceSettings, place, scope) || {};
      const viewSettings = yield select(PreferencesSelectors.selectPlaceViewSettings, place) || {};
      const activeViewId = yield select(PreferencesSelectors.selectActiveSavedViewId, place);
      const views = yield select(PreferencesSelectors.selectSavedViewsForPlace, place, scope) || [];
      const visibilitySettingsKey = FILTER_PLACE_TO_VISIBILITY_SETTINGS_MAPPING[place];
      const visibility = {};
      if (Array.isArray(visibilitySettingsKey)) {
        for (const key of visibilitySettingsKey) {
          visibility[key] = yield select(PreferencesSelectors.selectLeadsVisibilitySettings, key) || {};
        }
      }

      yield put({
        type: 'update',
        payload: {
          key: place,
          scope,
          value: {
            ...placeSettings,
            activeViewChanged: false,
            savedViews: views.map(v => {
              if (v?.id?.toString() === activeViewId?.toString()) {
                return {
                  ...v,
                  filters: viewSettings.filters,
                  preferences: {
                    ...viewSettings.preferences,
                    ...visibility,
                  },
                };
              }
              return v;
            }),
          },
        },
      });

      if (scope === SAVED_VIEW_SCOPES.PARTNER) {
        const placeSettingsForPrivate = yield select(PreferencesSelectors.selectPlaceSettings, place, SAVED_VIEW_SCOPES.PRIVATE) || {};
        yield put({
          type: 'update',
          payload: {
            key: place,
            scope: SAVED_VIEW_SCOPES.PRIVATE,
            value: {
              ...placeSettingsForPrivate,
              activeViewChanged: false,
            },
          },
        });
      }
    },
    * deleteView({ payload }, { call, put, select }) {
      const { place, viewId } = payload;
      const allViews = yield select(PreferencesSelectors.selectSavedViewsForPlace, place) || [];
      const scope = allViews.find(v => v.id.toString() === viewId.toString()).scope;
      const views = yield select(PreferencesSelectors.selectSavedViewsForPlace, place, scope) || [];
      const placeSettings = yield select(PreferencesSelectors.selectPlaceSettings, payload.place, scope) || {};
      yield put({
        type: 'update',
        payload: {
          key: place,
          scope,
          value: {
            ...placeSettings,
            filters: {},
            preferences: {},
            savedViews: views.filter(v => v.id.toString() !== payload.viewId.toString()),
          },
        },
      });
    },
    * saveNewView({ payload }, { call, put, select }) {
      const { scope, place } = payload;
      const currentViews = yield select(PreferencesSelectors.selectSavedViewsForPlace, place, scope) || [];
      const placeSettings = yield select(PreferencesSelectors.selectPlaceSettings, place) || {};
      const currentViewSettings = yield select(PreferencesSelectors.selectPlaceViewSettings, place) || {};
      // Selector results are memoized and may be frozen — copy before enriching
      const viewSettings = {
        ...currentViewSettings,
        preferences: { ...(currentViewSettings.preferences || {}) },
      };
      const visibilitySettingsKey = FILTER_PLACE_TO_VISIBILITY_SETTINGS_MAPPING[place];
      if (Array.isArray(visibilitySettingsKey)) {
        for (const key of visibilitySettingsKey) {
          viewSettings.preferences[key] = (yield select(PreferencesSelectors.selectLeadsVisibilitySettings, key)) || {};
        }
      }
      const viewId = new Date().getTime().toString();
      const activeViewValue = {
        activeView: viewId,
        activeViewChanged: false,
      };
      yield put({
        type: 'update',
        payload: {
          key: place,
          scope,
          value: {
            ...placeSettings,
            ...(scope === SAVED_VIEW_SCOPES.PRIVATE ? activeViewValue : {}),
            savedViews: [
              ...currentViews,
              {
                ...viewSettings,
                ...payload.data,
                id: viewId,
              },
            ],
          },
        },
      });

      if (scope === SAVED_VIEW_SCOPES.PARTNER) {
        const placeSettingsForPrivate = yield select(PreferencesSelectors.selectPlaceSettings, place, SAVED_VIEW_SCOPES.PRIVATE) || {};
        yield put({
          type: 'update',
          payload: {
            key: place,
            scope: SAVED_VIEW_SCOPES.PRIVATE,
            value: {
              ...placeSettingsForPrivate,
              ...activeViewValue,
            },
          },
        });
      }
    },
    // endregion Saved Views
    * pinCalendarAgent ({payload}, { call, put, select }) {
      const currentPinnedCalendarAgents = yield select(
        PreferencesSelectors.selectPinnedCalendarAgents,
      );
      if (currentPinnedCalendarAgents.includes(payload.id)) return
      yield put({
        type: 'update',
        payload: {
          key: 'pinnedCalendarAgents',
          value: {
            list: currentPinnedCalendarAgents.concat(payload.id),
          },
        },
      });
    },
    * unPinCalendarAgent ({payload}, { call, put, select }) {
      const currentPinnedCalendarAgents = yield select(
        PreferencesSelectors.selectPinnedCalendarAgents,
      );
      if (!currentPinnedCalendarAgents.includes(payload.id)) return
      yield put({
        type: 'update',
        payload: {
          key: 'pinnedCalendarAgents',
          value: {
            list: currentPinnedCalendarAgents.filter((id) => id !== payload.id),
          },
        },
      });
    },
    * fetchPartnerConfig(_, { call, put }) {
      const { response, data } = yield call(fetchPartnerConfigRequest);
      return yield processAPIResponse({ response, data }, {
        * onSuccess(data) {
          yield put({
            type: 'setPartnerConfig',
            payload: data,
          });
        },
      }, 'data');
    },
    * clearFilter({payload}, { call, put, select }) {
      const { key } = payload;
      const placeSettings = yield select(PreferencesSelectors.selectPlaceSettings, key) || {};
      yield put({
        type: 'modify',
        payload: {
          key: `ui.${key}`,
          value: {
            ...placeSettings,
            filters: {},
            preferences: {},
            activeView: null,
            activeViewChanged: false,
          },
        },
      });
    },
    * clearFilters({payload}, { put, select }) {
      const { key } = payload;
      const placeSettings = yield select(PreferencesSelectors.selectPlaceSettings, key) || {};
      const preserved = pick(get(placeSettings, 'filters', {}), GROUPING_FILTER_KEYS);
      const newPlaceSettings: Record<string, any> = {
        ...placeSettings,
        filters: preserved,
        activeView: null,
        activeViewChanged: false,
      };
      if (key === FILTER_PLACES.TEAM_VIEW_CALENDAR) {
        newPlaceSettings.preferences = {
          ...get(placeSettings, 'preferences', {}),
          excludedStates: { value: [] },
          excludedAgents: { list: [] },
        };
      }
      yield put({
        type: 'modify',
        payload: {
          key: `ui.${key}`,
          value: newPlaceSettings,
        },
      });
    },
    * fetchPartnerUserIntegrations(_, { call, put, select }) {
      const { response, data } = yield call(fetchPartnerUserIntegrationsRequest);
      return yield processAPIResponse({ response, data }, {
        * onSuccess(data) {
          yield put({
            type: 'setPartnerUserIntegrations',
            payload: data,
          });
        },
        // The endpoint needs `integrations_view`, which most agent roles lack.
        // Screens that gate on the list dispatch this in the background, so the
        // 403 is an expected answer — not something to interrupt the user with.
        * onError() {},
      }, 'data');
    },
    * fetchIntegrationTypes(_, { call, put, select }) {
      const { response, data } = yield call(fetchIntegrationTypesRequest);
      return yield processAPIResponse({ response, data }, {
        * onSuccess(data) {
          yield put({
            type: 'setIntegrationTypes',
            payload: data?.map(data => ({
              ...data,
              integration_type: data.type
            })),
          });
        },
      }, 'data');
    },
    * changePartnerConfig({ payload }, { call, put, select }) {
      const currentConfig = yield select(PreferencesSelectors.selectPartnerConfig);
      const { response, data } = yield call(setPartnerConfigRequest, {
        data: {
          ...currentConfig,
          ...omit(payload, ['then']),
        },
      });
      return yield processAPIResponse({ response, data }, {
        * onSuccess(data) {
          yield put({
            type: 'fetchPartnerConfig',
          });
          if (payload.then) payload.then();
        },
      });
    },
    * change({ payload }, { call, put, select }) {
      let newFilter = omit(payload, ['place', 'persist', 'mode', 'local']);
      const place = payload.place;
      const placeSettings = yield select(PreferencesSelectors.selectPlaceSettings, place) || {};
      /**
       * payload.mode can be filters / view etc. based on the requirement. Since the filters object are used
       * directly in the API Call payloads, it's important to have a clear distinction between the modes.
       * */
      if (payload.mode) {
        newFilter = {
          [payload.mode]: omit(newFilter, 'mode'),
        };
      }

      const viewKey = payload.mode ? 'preferences' : 'filters';
      const key = `ui.${place}`;

      const currentSavedView = yield select(PreferencesSelectors.selectActiveSavedViewId, place);
      const newPlaceSettings = {
        ...placeSettings,
        [viewKey]: {
          ...get(placeSettings, viewKey, {}),
          ...newFilter,
        },
      }
      if (currentSavedView) {
        yield put({
          type: 'merge',
          payload: {
            key,
            value: newPlaceSettings
          },
        });
        yield put({
          type: 'activeSavedViewChanged',
          payload: {
            place,
          },
        });
      }else {
        yield put({
          type: 'update',
          payload: {
            key: payload.place,
            value: newPlaceSettings
          },
        });
      }

    },
    * activeSavedViewChanged({ payload }, { put, select }) {
      const { place } = payload;
      const placeSettings = yield select(PreferencesSelectors.selectPlaceSettings, place) || {};
      yield put({
        type: 'modify',
        payload: {
          key: `ui.${place}`,
          value: {
            ...placeSettings,
            activeViewChanged: Date.now(),
          },
        },
      });
    },
    * update({ payload }, { call, put, select }) {
      const { key, value, listKey = 'ui', silent, scope } = payload;

      if (key === 'calendarUISettings') {
        yield put({
          type: 'change',
          payload: {
            place: FILTER_PLACES.TEAM_VIEW_CALENDAR,
            mode: 'calendarUISettings',
            ...(value || {}),
          }
        });
        return;
      }

      if (VISIBILITY_SETTINGS_PLACES_TO_FILTER_PLACE_MAPPING[key] && !silent) {
        yield put({
          type: 'activeSavedViewChanged',
          payload: {
            place: VISIBILITY_SETTINGS_PLACES_TO_FILTER_PLACE_MAPPING[key],
          },
        });
      }

      // Detect if calendar visibility settings toggled on an extra field to trigger calendar refresh
      let shouldRefetchCalendar = false;
      const isCalendarVisibilityKey = [
        LEAD_VISIBILITY_SETTINGS_PLACES.CALENDAR_EVENT_POPOVER,
        LEAD_VISIBILITY_SETTINGS_PLACES.CALENDAR_CARD,
      ].includes(key);

      if (isCalendarVisibilityKey && scope !== SAVED_VIEW_SCOPES.PARTNER) {
        try {
          const prevSettings = (yield select(PreferencesSelectors.selectLeadsVisibilitySettings, key)) || {};
          const prevShown: string[] = prevSettings?.shown || [];
          const newShown: string[] = (value && value.shown) || [];
          if (Array.isArray(newShown)) {
            shouldRefetchCalendar = newShown.some((f) => typeof f === 'string' && f.startsWith('extraField:') && !prevShown.includes(f));
          }
        } catch (e) {
          // no-op: in doubt, do not trigger refetch
          shouldRefetchCalendar = false;
        }
      }

      if (scope === SAVED_VIEW_SCOPES.PARTNER) {
        yield put({
          type: 'modifyPartnerPreferences',
          payload: {
            key,
            value,
          },
        });
        yield call(setPartnerPreferencesRequest, { params: { key }, data: value });
      } else {
        yield put({
          type: 'modify',
          payload: {
            key: `${listKey}.${key}`, value,
          },
        });
        // After state is updated, if needed, refetch calendar events to include extra field values
        if (shouldRefetchCalendar) {
          yield put({ type: 'serviceRequest/fetchCalendarViewEvents' });
        }
        yield call(setUiPreferencesRequest, { params: { key }, data: value });
      }

    },
  },
  reducers: {
    set(state, action) {
      return {
        ...state,
        initialized: true,
        list: {
          ...state.list,
          ...(action.payload || {}),
        },
      };
    },
    resetPlaceFilters(state, action) {
      return {
        ...state,
        list: {
          ...(state.list || {}),
          ui: {
            ...get(state.list, 'ui', {}),
            [action.payload.place]: pick(get(state.list, `ui.${action.payload.place}`, {}), ['savedViews'])
          }
        },
      }
    },
    modify(state, action) {
      const newList = set(cloneDeep(state.list), action.payload.key, action.payload.value);
      return {
        ...state,
        list: newList,
      };
    },
    modifyPartnerPreferences(state, action) {
      return {
        ...state,
        partnerConfig: {
          ...state.partnerConfig,
          [action.payload.key]: action.payload.value,
        },
      };
    },
    omit(state, action) {
      return {
        ...state,
        list: omit(state.list, action.payload.key),
      };
    },
    merge(state, action) {
      const newList = set(cloneDeep(state.list), action.payload.key, {
        ...get(state.list, action.payload.key, {}),
        ...action.payload.value,
      });
      return {
        ...state,
        list: newList,
      };
    },
    setPartnerConfig(state, action) {
      return {
        ...state,
        partnerConfig: action.payload,
      };
    },
    setPartnerUserIntegrations(state, action) {
      return {
        ...state,
        userIntegrations: action.payload,
      };
    },
    setIntegrationTypes(state, action) {
      return {
        ...state,
        integrationTypes: action.payload,
      };
    },
    reset() {
      return initialState;
    },
  },
};
